import React, { useState, useEffect } from "react";
import { Box, styled, Chip } from "@mui/material";
import { useSelector } from "react-redux";
import { useStepImgDiffQuery } from "../../../useQuery";
import { useAuthedMediaSrc } from "../../../hooks";
import { IMAGE_AVAILABILITY_MESSAGE } from "../../../constants/imageAvailabilityReason";

const PLACEHOLDER_IMAGE = "/_No_Image__placeholder.png";

const StyledBox = styled(Box)`
  position: absolute;
  padding: 6px 12px;
  border-radius: 25px;
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(2px);
  min-width: 150px;
  font-size: 12px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #444141;
  letter-spacing: 0.9px;
  bottom: 10px;
`;

const ReasonBox = styled(Box)`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(255, 245, 230, 0.95);
  font-size: 11px;
  text-align: center;
  color: #8a5a1d;
  border: 1px solid rgba(0, 0, 0, 0.08);
  max-width: 90%;
`;

const ImageContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
`;

const StyledImage = styled("img")`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  width: auto;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }
`;

const AnalysisBox = styled(Box)`
  margin-top: 16px;
  padding: 16px;
  border-radius: 8px;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
`;

const StatusBadge = styled(Box)`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 12px;
`;

const DetailRow = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled(Box)`
  font-weight: 600;
  color: #666;
  min-width: 140px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled(Box)`
  flex: 1;
  color: #333;
  font-size: 13px;
  word-break: break-word;
`;

const Screenshot = ({ fromExcReports, currentEditItem, fromImageDiff, refreshedResults }) => {
  const tsrData = useSelector((state) => state.authoringSlice.tsrData);
  const currentEditItemIndex = useSelector((state) => state.authoringSlice.editItemIndex);
  const imgeDiffTcid = useSelector((state) => state.executionSlice.selectedTestCase);

  const similarity = currentEditItem?.similarity;

  const similarityColor =
    similarity === 100
      ? "#1e7e34"
      : similarity < 90
        ? "#8a1f1f"
        : "#8a6d1d";

  // Raw backend URLs — resolved into renderable blob URLs by useAuthedMediaSrc
  // below, which is what attaches the Authorization header.
  const [beforeImageUrl, setBeforeImageUrl] = useState(
    tsrData[currentEditItemIndex]?.screenshot1 || null,
  );

  const [afterImageUrl, setAfterImageUrl] = useState(
    tsrData[currentEditItemIndex]?.Failure_Screenshot_Location || null,
  );

  const { refetchWithParams: getImgDiff, data } = useStepImgDiffQuery();

  const diffSummary = data?.diffsummary;
  const comparisonStatus = data?.comparison_status;
  const numericConfidence = data?.numeric_confidence;
  const message = data?.message;
  const isNotFirstStep = currentEditItem?.Step_Number > 1;
  const showDiffSummary = fromImageDiff && isNotFirstStep && Boolean(diffSummary);

  const getStatusColor = (status) => {
    switch (status) {
      case "PASS":
        return {
          background: "#e8f5e9",
          color: "#1e7e34",
          borderColor: "#1e7e34",
        };
      case "WARNING":
        return {
          background: "#fff3e0",
          color: "#e65100",
          borderColor: "#e65100",
        };
      case "FAIL":
        return {
          background: "#ffebee",
          color: "#c62828",
          borderColor: "#c62828",
        };
      case "ERROR":
        return {
          background: "#f5f5f5",
          color: "#616161",
          borderColor: "#616161",
        };
      default:
        return {
          background: "#f5f5f5",
          color: "#616161",
          borderColor: "#616161",
        };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "PASS":
        return "✅";
      case "WARNING":
        return "⚠️";
      case "FAIL":
        return "❌";
      case "ERROR":
        return "⚠️";
      default:
        return "ℹ️";
    }
  };

  const statusColors = comparisonStatus ? getStatusColor(comparisonStatus) : null;

  useEffect(() => {
    if (!fromImageDiff) return;

    getImgDiff({
      tsid: currentEditItem.Test_Step,
      stepNumber: currentEditItem.Step_Number,
      tcid: imgeDiffTcid,
      tcrid: currentEditItem.Test_Case_Results_Id,
    });
  }, [
    fromImageDiff,
    currentEditItem?.Test_Step,
    currentEditItem?.Step_Number,
    currentEditItem?.Test_Case_Results_Id,
    imgeDiffTcid,
  ]);

  useEffect(() => {
    setBeforeImageUrl(tsrData[currentEditItemIndex]?.screenshot1 || null);
    setAfterImageUrl(tsrData[currentEditItemIndex]?.Failure_Screenshot_Location || null);
  }, [tsrData, currentEditItemIndex]);

  const getBeforeImageUrl = () => {
    if (fromImageDiff) {
      return data?.image1 || null;
    } else if (fromExcReports) {
      return currentEditItem.screenshot1 || null;
    } else {
      return beforeImageUrl;
    }
  };

  const getAfterImageUrl = () => {
    if (fromImageDiff) {
      return data?.image2 || null;
    } else if (fromExcReports) {
      return currentEditItem.Failure_Screenshot_Location || null;
    } else {
      return afterImageUrl;
    }
  };

  const refreshedRow = refreshedResults?.find(
    (row) => row.Step_Number === currentEditItem?.Step_Number,
  );

  const getBeforeImageStatus = () => {
    if (fromImageDiff) {
      return data?.image1_status || null;
    } else if (fromExcReports) {
      return refreshedRow?.screenshot1_status || currentEditItem?.screenshot1_status || null;
    } else {
      return tsrData[currentEditItemIndex]?.screenshot1_status || null;
    }
  };

  const getAfterImageStatus = () => {
    if (fromImageDiff) {
      return data?.image2_status || null;
    } else if (fromExcReports) {
      return (
        refreshedRow?.Failure_Screenshot_Location_status ||
        currentEditItem?.Failure_Screenshot_Location_status ||
        null
      );
    } else {
      return tsrData[currentEditItemIndex]?.Failure_Screenshot_Location_status || null;
    }
  };

  const { src: beforeSrc } = useAuthedMediaSrc(getBeforeImageUrl(), {
    fallback: PLACEHOLDER_IMAGE,
  });
  const { src: afterSrc } = useAuthedMediaSrc(getAfterImageUrl(), { fallback: PLACEHOLDER_IMAGE });

  return (
    <>
      <Box
        border="2px solid rgba(0,0,0,0.3)"
        borderRadius="12px"
        height={500}
        display="flex"
        overflow="hidden"
      >
        <ImageContainer borderRight="2px solid blue">
          <StyledImage
            src={beforeSrc || PLACEHOLDER_IMAGE}
            onClick={() => window.open(beforeSrc || PLACEHOLDER_IMAGE, "_blank")}
          />
          {!getBeforeImageUrl() && getBeforeImageStatus() && (
            <ReasonBox>
              {IMAGE_AVAILABILITY_MESSAGE[getBeforeImageStatus()] || getBeforeImageStatus()}
            </ReasonBox>
          )}
          <StyledBox sx={{ bottom: 10 }}>
            {fromImageDiff ? "Latest Image" : "Element Selected"}
          </StyledBox>
        </ImageContainer>

        <ImageContainer>
          <StyledImage
            src={afterSrc || PLACEHOLDER_IMAGE}
            onClick={() => window.open(afterSrc || PLACEHOLDER_IMAGE, "_blank")}
          />
          {!getAfterImageUrl() && getAfterImageStatus() && (
            <ReasonBox>
              {IMAGE_AVAILABILITY_MESSAGE[getAfterImageStatus()] || getAfterImageStatus()}
            </ReasonBox>
          )}
          <StyledBox sx={{ bottom: 10 }}>
            {fromImageDiff ? "Baseline Image" : "Action Completed"}
          </StyledBox>
        </ImageContainer>
      </Box>

      {showDiffSummary && (
        <AnalysisBox>
          {/* Status Badge */}
          {comparisonStatus && statusColors && (
            <StatusBadge
              sx={{
                background: statusColors.background,
                color: statusColors.color,
                border: `1px solid ${statusColors.borderColor}`,
              }}
            >
              {getStatusIcon(comparisonStatus)} {comparisonStatus}
            </StatusBadge>
          )}

          <Box>
            {numericConfidence && (
              <DetailRow>
                <DetailLabel>Confidence Score:</DetailLabel>
                <DetailValue>
                  <Box
                    sx={{
                      fontWeight: 600,
                      color:
                        parseFloat(numericConfidence) >= 90
                          ? "#1e7e34"
                          : parseFloat(numericConfidence) >= 50
                            ? "#e65100"
                            : "#c62828",
                    }}
                  >
                    {numericConfidence}%
                  </Box>
                </DetailValue>
              </DetailRow>
            )}

            {message && (
              <DetailRow>
                <DetailLabel>Analysis:</DetailLabel>
                <DetailValue>{message}</DetailValue>
              </DetailRow>
            )}

            {diffSummary && (
              <DetailRow>
                <DetailLabel>Summary:</DetailLabel>
                <DetailValue sx={{ fontSize: "12px", color: "#666" }}>{diffSummary}</DetailValue>
              </DetailRow>
            )}

            {similarity != null && (
              <DetailRow>
                <DetailLabel>Legacy Score:</DetailLabel>
                <DetailValue>
                  <Box sx={{ fontWeight: 600, color: similarityColor }}>{similarity}%</Box>
                </DetailValue>
              </DetailRow>
            )}
          </Box>

          {comparisonStatus && (
            <Box
              sx={{
                marginTop: 2,
                padding: "12px",
                backgroundColor:
                  comparisonStatus === "PASS"
                    ? "#e8f5e9"
                    : comparisonStatus === "FAIL"
                      ? "#ffebee"
                      : "#fff3e0",
                borderLeft:
                  comparisonStatus === "PASS"
                    ? "4px solid #1e7e34"
                    : comparisonStatus === "FAIL"
                      ? "4px solid #c62828"
                      : "4px solid #e65100",
                borderRadius: "4px",
              }}
            >
              {comparisonStatus === "PASS" && (
                <Box sx={{ color: "#1e7e34", fontSize: "12px", fontWeight: 600 }}>
                  ✅ Test Passed - Images match successfully
                </Box>
              )}
              {comparisonStatus === "WARNING" && (
                <Box sx={{ color: "#e65100", fontSize: "12px", fontWeight: 600 }}>
                  ⚠️ Review Recommended - Some visual differences detected
                </Box>
              )}
              {comparisonStatus === "FAIL" && (
                <Box sx={{ color: "#c62828", fontSize: "12px", fontWeight: 600 }}>
                  ❌ Test Failed - Images are significantly different
                </Box>
              )}
              {comparisonStatus === "ERROR" && (
                <Box sx={{ color: "#616161", fontSize: "12px", fontWeight: 600 }}>
                  ⚠️ Unable to compare images - {message}
                </Box>
              )}
            </Box>
          )}
        </AnalysisBox>
      )}
    </>
  );
};

export default Screenshot;
